<template>
  <div class="rf-settings-content">
    <div v-for="report in reports" :key="report.type" class="mt-10 flex flex-col gap-5">
      <div class="rf-settings-section-title">
        <h4>{{ report.title }}</h4>
      </div>
      <div class="font-small grid grid-cols-4 items-center gap-4 text-sm">
        <div class="col-span-3 space-y-4 text-sm">
          <p>{{ report.description }}</p>

          <!-- Email Recipients Section -->
          <div class="mb-5">
            <div class="text-grey-1 font-semibold">Email Recipients</div>
            <div class="font-small mt-2 flex h-10 items-center gap-5 text-sm">
              <RfCheckbox v-model="formState[report.type].send_to_company_admins">
                Company Admins
              </RfCheckbox>
              <RfCheckbox v-model="formState[report.type].send_to_app_admins">
                App Admins
              </RfCheckbox>
              <RfCheckbox v-model="formState[report.type].custom_checked">
                Custom Emails
              </RfCheckbox>

              <RfInput
                v-if="formState[report.type].custom_checked"
                v-model="formState[report.type].custom_emails"
                placeholder="email1@example.com,email2@example.com"
                variant="outlined"
              />
            </div>
          </div>

          <!-- Email Frequency Section -->
          <div v-if="report.allowed_frequencies.length">
            <RfRadioGroup
              v-model="formState[report.type].frequency"
              legend="Email Frequency"
              :options="[
                ...(report.allowed_frequencies.includes('daily')
                  ? [{ value: 'daily', label: 'Daily' }]
                  : []),
                ...(report.allowed_frequencies.includes('weekly')
                  ? [{ value: 'weekly', label: 'Weekly' }]
                  : []),
                ...(report.allowed_frequencies.includes('monthly')
                  ? [{ value: 'monthly', label: 'Monthly' }]
                  : []),
              ]"
            />
          </div>
        </div>

        <!-- Manual Notification Send -->
        <div class="col-span-1 mt-4 mr-4 flex justify-end">
          <RfButtonNew
            size="small"
            type="secondary"
            :disabled="sendNowTooltip(report.type)"
            tooltipMessage="Click to send the notification now"
            @click="sendAppNotification(report.type)"
          >
            <RfPaperPlaneIcon class="h-4! w-4!" />
            Send Now
          </RfButtonNew>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { isEqual, cloneDeep } from "lodash-es";
import { useToastsStore } from "@/pinia/toastsStore";
import { useAppsStore } from "@/pinia/appsStore";
import RfCheckbox from "../inputs/RfCheckbox.vue";
import RfInput from "../inputs/RfInput.vue";
import RfRadioGroup from "../inputs/RfRadioGroup.vue";
import RfButtonNew from "../buttons/RfButtonNew.vue";
import RfPaperPlaneIcon from "../icons/RfPaperPlaneIcon.vue";

const props = defineProps({
  appNotifications: {
    type: Array,
    default: () => [],
  },
  app: {
    type: Object,
    required: true,
  },
});

const toastsStore = useToastsStore();
const appsStore = useAppsStore();

const reportsConst = [
  {
    type: "expiring_prompts",
    title: "Expiring Prompts",
    description: "Get email notifications for prompts expiring within the next 3 days.",
    allowed_frequencies: ["daily"],
  },
  {
    type: "expiring_guides",
    title: "Expiring Guides",
    description: "Get email notifications for guides expiring within the next 3 days.",
    allowed_frequencies: ["daily"],
  },
  {
    type: "prompt_performance_delta",
    title: "Prompt Performance Delta Report",
    description: "Get an emailed report of prompts with large performance changes.",
    allowed_frequencies: ["daily", "weekly"],
  },
];

const reports = computed(() => {
  const reportsDynamic = [];

  // Only include connector error report if flag is enabled
  if (props.app?.flags?.report_connector_errors) {
    reportsDynamic.push({
      type: "aggregated_connector_errors",
      title: "Connector Error Report",
      description: "Get an emailed CSV report of connector errors from the last 14 days.",
      allowed_frequencies: ["monthly"],
    });
  }

  return [...reportsConst, ...reportsDynamic];
});

const ensureFormStateInitialized = () => {
  reports.value.forEach(report => {
    if (!formState.value[report.type]) {
      formState.value[report.type] = {
        send_to_company_admins: false,
        send_to_app_admins: false,
        custom_checked: false,
        custom_emails: "",
        frequency: report.allowed_frequencies[0] || "daily",
      };
    }
  });
};

const formState = ref({});
const initialState = ref({});

const isSame = computed(() => {
  const normalized = state => {
    return reports.value.map(report => {
      const r = state[report.type];
      return {
        send_to_company_admins: r.send_to_company_admins,
        send_to_app_admins: r.send_to_app_admins,
        custom_emails: r.custom_checked ? r.custom_emails : "",
        frequency: r.frequency,
      };
    });
  };

  return isEqual(normalized(formState.value), normalized(initialState.value));
});

const hasRecipients = reportType => {
  const reportState = formState.value[reportType];
  const isNotEmpty = val => typeof val === "string" && val.trim() !== "";

  return (
    reportState.send_to_company_admins ||
    reportState.send_to_app_admins ||
    (reportState.custom_checked && isNotEmpty(reportState.custom_emails))
  );
};

const sendNowTooltip = reportType => {
  if (!hasRecipients(reportType)) return "You must select recipients and save changes";
  if (!isSame.value) return "You must save changes before sending";
  return null;
};

const submit = async () => {
  const payload = {
    app_notifications: reports.value.map(report => {
      const state = formState.value[report.type];
      return {
        report_type: report.type,
        frequency: state.frequency,
        send_to_company_admins: state.send_to_company_admins,
        send_to_app_admins: state.send_to_app_admins,
        custom_emails: state.custom_checked ? state.custom_emails : "",
      };
    }),
  };

  try {
    const updatedNotifications = await appsStore.updateAppNotifications({
      appId: props.app.id,
      appNotifications: payload,
    });
    toastsStore.create({ type: "success", body: "App notifications updated!" });
    reports.value.forEach(report => {
      const updated = updatedNotifications.find(n => n.report_type === report.type);

      if (updated) {
        formState.value[report.type] = {
          send_to_company_admins: updated.send_to_company_admins || false,
          send_to_app_admins: updated.send_to_app_admins || false,
          custom_emails: (updated.custom_emails || []).join(", "),
          custom_checked: updated.custom_emails?.length > 0,
          frequency: updated.frequency || "daily",
        };
      }
    });

    initialState.value = cloneDeep(formState.value);
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
};

const sendAppNotification = async reportType => {
  try {
    await appsStore.sendAppNotification({ appId: props.app.id, reportType: reportType });
    toastsStore.create({ type: "success", body: "App notification queued!" });
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
};

watch(
  () => props.appNotifications,
  notifications => {
    ensureFormStateInitialized();
    reports.value.forEach(report => {
      const notification = notifications.find(n => n.report_type === report.type);

      if (notification) {
        formState.value[report.type].send_to_company_admins =
          notification.send_to_company_admins || false;

        formState.value[report.type].send_to_app_admins = notification.send_to_app_admins || false;

        formState.value[report.type].custom_emails = (notification.custom_emails || []).join(", ");

        formState.value[report.type].custom_checked = notification.custom_emails?.length > 0;

        formState.value[report.type].frequency = notification.frequency || "daily";
      }
    });

    initialState.value = cloneDeep(formState.value);
  },
  { immediate: true },
);

defineExpose({ submit, disabled: isSame });
</script>

<style scoped>
::v-deep(.v-input .v-selection-control) {
  font-size: 14px;
}

::v-deep(.v-selection-control__wrapper) {
  padding: 0;
  margin: 0;
}

::v-deep(.v-label) {
  font-size: 14px;
  line-height: 1.2;
}
</style>
